import type React from "react";
import { useCallback, useRef, useState } from "react";
import type { SlashCommandDropdownRef } from "../../ui/slash-command-dropdown.tsx";
import type { ContactDropdownPosition } from "../../ui/contact-autocomplete.tsx";

interface UseSlashSystemOptions {
  onChange: (value: string) => void;
  getDropdownPosition: (charOffset?: number, text?: string) => ContactDropdownPosition | null;
  cursorTimeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
}

export function useSlashSystem({
  onChange,
  getDropdownPosition,
  cursorTimeoutRef,
  textareaRef,
}: UseSlashSystemOptions) {
  const [slashQuery, setSlashQuery] = useState<string | null>(null);
  const [slashDropdownPos, setSlashDropdownPos] = useState<ContactDropdownPosition | null>(null);
  const dropdownRef = useRef<SlashCommandDropdownRef>(null);

  const close = useCallback(() => {
    setSlashQuery(null);
    setSlashDropdownPos(null);
  }, []);

  const isOpen = slashQuery !== null;

  /** Returns true if input was consumed by slash detection. */
  const onInputChange = useCallback(
    (text: string, textBeforeCursor: string): boolean => {
      if (text.startsWith("/")) {
        const queryText = textBeforeCursor.slice(1);
        if (!queryText.includes(" ") && !queryText.includes("\n")) {
          setSlashQuery(queryText);
          setSlashDropdownPos(getDropdownPosition());
          return true;
        }
      }
      // Close slash dropdown if conditions no longer met
      setSlashQuery(null);
      setSlashDropdownPos(null);
      return false;
    },
    [getDropdownPosition],
  );

  /** Returns true if the key event was handled. */
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent): boolean => {
      if (!isOpen || !dropdownRef.current) return false;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        dropdownRef.current.handleArrowDown();
        return true;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        dropdownRef.current.handleArrowUp();
        return true;
      }
      if (e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) {
        e.preventDefault();
        const wasSelected = dropdownRef.current.handleEnter();
        if (wasSelected) return true;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return true;
      }
      return false;
    },
    [isOpen, close],
  );

  const handleSelect = useCallback(
    (commandName: string) => {
      const newValue = `/${commandName} `;
      onChange(newValue);
      close();

      if (textareaRef.current) {
        const newCursorPos = newValue.length;
        if (cursorTimeoutRef.current) clearTimeout(cursorTimeoutRef.current);
        cursorTimeoutRef.current = setTimeout(() => {
          textareaRef.current?.focus();
          textareaRef.current?.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
      }
    },
    [onChange, close, textareaRef, cursorTimeoutRef],
  );

  const handleGuidedFlowTrigger = useCallback(
    (triggerWord: string) => {
      close();
      onChange(triggerWord);
    },
    [onChange, close],
  );

  return {
    isOpen,
    close,
    onKeyDown,
    onInputChange,
    query: slashQuery,
    dropdownPos: slashDropdownPos,
    handleSelect,
    handleGuidedFlowTrigger,
    dropdownRef,
  };
}
